<script setup lang="ts">
import { ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { logout } from '@/shared/lib/auth';
import { useAuth } from '@/shared/stores/auth';
import AppHeader from './ui/AppHeader.vue';
import MenuSection from './ui/MenuSection.vue';
import MenuItem from './ui/MenuItem.vue';
import AppBottomNavigation from './ui/AppBottomNavigation.vue';
import { BaggageQrScannerModal } from '@/features/baggageQrScan';
import { ErrorSnackbar } from '@/shared/ui';
import { useLuggageQrSearch } from '@/features/luggageQrSearch';
import { fetchApplicationDetail } from '@/entities/application';
import ClosingModal from '@/pages/admin/top/ui/ClosingModal.vue';
import { useClosingStatusStore } from '@/shared/stores/closingStatus';
import { storeToRefs } from 'pinia';

defineOptions({ name: 'HasNavigationLayout' });

const router = useRouter();
const { user, role } = useAuth();
const { xs } = useDisplay();
const menuOpen = ref(false);
const isQrScannerOpen = ref(false);
const isLuggageQrScannerOpen = ref(false);
const showLuggageQrError = ref(false);
const { handleLuggageQrDetect, errorMessage: luggageQrError } = useLuggageQrSearch();

// 締め処理モーダル関連
const isClosingModalOpen = ref(false);
const isClosingLoading = ref(false);
const closingStore = useClosingStatusStore();
const { airportCounters, selectedCounterId, isClosingCompleted, isPastClosingTime } = storeToRefs(closingStore);
const { initializeCounters, checkClosingStatus, executeClosing, revertClosing } = closingStore;

const handleMenuToggle = () => {
    menuOpen.value = !menuOpen.value;
};

const handleLogout = () => {
    logout();
};

const handleMenu = (value: boolean) => (menuOpen.value = value);

const handleOpenUserQrScanner = () => {
    menuOpen.value = false;
    isQrScannerOpen.value = true;
};

const handleOpenLuggageQrScanner = () => {
    menuOpen.value = false;
    isLuggageQrScannerOpen.value = true;
};

const handleLuggageQrScan = async (qrCode: string) => {
    const success = await handleLuggageQrDetect(qrCode);
    if (success) {
        isLuggageQrScannerOpen.value = false;
    } else {
        showLuggageQrError.value = true;
    }
};

const showUserQrError = ref(false);
const userQrErrorMessage = ref('');

const handleUserQrScan = async (applicationId: string) => {
    try {
        const applicationDetail = await fetchApplicationDetail(applicationId);
        const status = applicationDetail.delivery_status;

        if (status === 'applied') {
            isQrScannerOpen.value = false;
            router.push({ name: 'application.traveler-consent', params: { applicationId } });
        } else if (status === 'hotel_checked') {
            isQrScannerOpen.value = false;
            router.push({ name: 'application.traveler-handover', params: { applicationId } });
        } else {
            showUserQrError.value = true;
            userQrErrorMessage.value = '該当するQRコードがありません';
        }
    } catch {
        showUserQrError.value = true;
        userQrErrorMessage.value = '該当するQRコードがありません';
    }
};

const handleOpenClosingModal = async () => {
    menuOpen.value = false;
    await initializeCounters({
        isAirport: role.value.isAirport,
        assignedCounterIds: user.value?.airport_reception_ids ?? [],
    });
    await checkClosingStatus();
    isClosingModalOpen.value = true;
};

const handleExecuteClosing = async () => {
    isClosingLoading.value = true;
    try {
        await executeClosing();
        isClosingModalOpen.value = false;
    } finally {
        isClosingLoading.value = false;
    }
};

const handleRevertClosing = async () => {
    isClosingLoading.value = true;
    try {
        await revertClosing();
        isClosingModalOpen.value = false;
    } finally {
        isClosingLoading.value = false;
    }
};

const handleCounterChange = async () => {
    await checkClosingStatus();
};
</script>

<template>
    <AppHeader @menu-toggle="handleMenuToggle" />

    <v-main class="main-content">
        <slot></slot>
    </v-main>

    <AppBottomNavigation
        v-if="xs"
        @open-user-qr-scanner="handleOpenUserQrScanner"
        @open-luggage-qr-scanner="handleOpenLuggageQrScanner"
    />

    <v-navigation-drawer :model-value="menuOpen" temporary location="end" width="389" class="hamburger-menu">
        <v-container class="d-flex justify-end pa-4">
            <v-btn icon="mdi-close" variant="text" size="small" @click="handleMenu(false)" color="white" />
        </v-container>

        <v-container class="pa-4 pt-0">
            <!-- 共通メニュー -->
            <MenuSection title="共通メニュー">
                <MenuItem to="/admin">トップへ戻る</MenuItem>
                <!-- ユーザQR: hotel, at-airport, at-admin -->
                <v-list-item
                    v-if="role.isAdmin || role.isHotel || role.isAirport"
                    class="menu-item"
                    density="comfortable"
                    @click="handleOpenUserQrScanner"
                >
                    <v-list-item-title>ユーザQRコード読み取り</v-list-item-title>
                </v-list-item>
                <!-- 荷物QR: hotel, at-sc, at-admin -->
                <v-list-item
                    v-if="role.isAdmin || role.isHotel || role.isSC"
                    class="menu-item"
                    density="comfortable"
                    @click="handleOpenLuggageQrScanner"
                >
                    <v-list-item-title>荷物QRコード読み取り</v-list-item-title>
                </v-list-item>
            </MenuSection>

            <!-- ホテルメニュー: hotel, at-admin のみ表示 -->
            <MenuSection v-if="role.isAdmin || role.isHotel" title="ホテルメニュー">
                <MenuItem to="/admin/inspection">バゲージの受取・検品</MenuItem>
                <MenuItem to="/admin/authentication-codes">認証コード一覧</MenuItem>
            </MenuSection>

            <!-- 配送メニュー: at-sc, at-admin のみ表示 -->
            <MenuSection v-if="role.isAdmin || role.isSC" title="配送メニュー">
                <MenuItem to="/vehicles/select">ルート一覧</MenuItem>
            </MenuSection>

            <!-- 空港カウンターメニュー: at-airport, at-admin のみ表示 -->
            <MenuSection v-if="role.isAdmin || role.isAirport" title="空港カウンターメニュー">
                <v-list-item class="menu-item" density="comfortable" @click="handleOpenClosingModal">
                    <v-list-item-title>受付締め処理</v-list-item-title>
                </v-list-item>
            </MenuSection>

            <!-- 管理メニュー: at-admin のみ表示 -->
            <MenuSection v-if="role.isAdmin" title="管理メニュー">
                <MenuItem to="/admin/master-management">マスタ管理</MenuItem>
                <!-- スタッフ管理は at-admin のみ（at-sc-admin には表示しない） -->
                <MenuItem v-if="role.isAtAdmin" to="/admin/staffs">スタッフ管理</MenuItem>
            </MenuSection>

            <!-- その他: 全員 -->
            <MenuSection title="その他">
                <v-list-item
                    class="menu-item"
                    density="comfortable"
                    href="https://air-luggage.net/#faq"
                    target="_blank"
                >
                    <v-list-item-title>FAQ</v-list-item-title>
                </v-list-item>
                <v-list-item
                    class="menu-item"
                    density="comfortable"
                    href="https://air-luggage.net/inquiries-exclusively-for-contracts/"
                    target="_blank"
                >
                    <v-list-item-title>問い合わせフォーム</v-list-item-title>
                </v-list-item>
                <v-list-item class="menu-item logout-item" density="comfortable" @click="handleLogout">
                    <v-list-item-title>ログアウト</v-list-item-title>
                </v-list-item>
            </MenuSection>
        </v-container>
    </v-navigation-drawer>
    <!-- TODO: もはやBaggageに限らないのでリネームする -->
    <BaggageQrScannerModal v-model:is-open="isQrScannerOpen" :scanned-items="[]" @detect="handleUserQrScan" />
    <BaggageQrScannerModal v-model:is-open="isLuggageQrScannerOpen" :scanned-items="[]" @detect="handleLuggageQrScan" />

    <ErrorSnackbar v-model="showLuggageQrError">
        {{ luggageQrError }}
    </ErrorSnackbar>

    <ErrorSnackbar v-model="showUserQrError">
        {{ userQrErrorMessage }}
    </ErrorSnackbar>

    <ClosingModal
        v-model="isClosingModalOpen"
        v-model:selected-counter-id="selectedCounterId"
        :is-closing-completed="isClosingCompleted"
        :is-past-closing-time="isPastClosingTime"
        :is-loading="isClosingLoading"
        :airport-counters="airportCounters"
        @execute="handleExecuteClosing"
        @revert="handleRevertClosing"
        @counter-change="handleCounterChange"
    />
</template>

<style lang="css" scoped>
.main-content {
    background-color: #f2f2f7;
    height: calc(100vh - 64px);
    overflow: hidden;
}

@media (max-width: 600px) {
    .main-content {
        height: calc(100vh - 56px - 56px);
        background-color: #ffffff;
    }
}
</style>
